import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { toRem } from '@front/common/src/utils/styles';
import colors, { withOpacity } from '@front/common/src/utils/colors';

import {
  getAppointmentAccordionTitle,
  getAppointmentDescriptionIfNoSecondary,
} from '@front/customer/src/utils/basket';
import { CommonCleaningBasketProps } from '@front/customer/src/components/basket/types';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import { route } from '../../../../steps/steps';
import useBasketSection from '../../../../hooks/use-basket-section';

import CleaningAppointmentDetails from './multiple-cleaning-appointment-details';

const t = translate('basket');

const StyledAccordion = styled(Accordion)`
  margin: ${toRem(20)} 0;
`;

type CleaningAppointmentsProps = Pick<
  CommonCleaningBasketProps,
  'appointment' | 'weeklyRecurrence' | 'appointmentDate'
>;

const MultipleCleaningAppointments = ({
  appointment,
  weeklyRecurrence,
  appointmentDate,
}: CleaningAppointmentsProps) => {
  const { primary, secondary } = appointment;
  const { isOpen, isVisible } = useBasketSection({
    data: !!primary,
    sectionLocations: [route('appointment_calendar'), route('appointment_hour')],
  });

  const { pathname } = useLocation();

  const subTitleKey =
    pathname === route('appointment_hour')
      ? '.cleaning.appointment_hour.recurring_subtitle'
      : '.secondary_appointment';

  const subtitle = secondary.length ? <Text.SM>{t(subTitleKey, { count: secondary.length })}</Text.SM> : undefined;

  if (!isVisible) return null;

  return (
    <>
      <Separator />
      <StyledAccordion
        icon='clock-filled'
        iconLabel={t('.appointment_icon_label')}
        title={getAppointmentAccordionTitle({ primary, appointmentDate, weeklyRecurrence })}
        subtitle={subtitle}
        isOpen={isOpen}
      >
        {secondary.length > 0 ? (
          <CleaningAppointmentDetails appointment={appointment} weeklyRecurrence={weeklyRecurrence} />
        ) : (
          <Text.SM color={withOpacity(colors.ink100, 0.5)}>
            {getAppointmentDescriptionIfNoSecondary({
              primary,
              appointmentDate,
              weeklyRecurrence,
              isOnCalendarScreen: pathname === route('appointment_calendar'),
            })}
          </Text.SM>
        )}
      </StyledAccordion>
    </>
  );
};

export default MultipleCleaningAppointments;
